import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import SyncIcon from '@mui/icons-material/Sync';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import { useDispatch } from 'react-redux';
import {
  getAcademicCalendar,
  getLeadMinDetails,
  getRescheduledCalendars,
  getSubjectEnrollmentIds,
} from '../../redux/features/CRDslice';
import { useSelector } from 'react-redux';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import Loader3D from '../../components/Loader/Loader3D';
import NoDataFound from '../../components/NodataIndication/NoDataFound';
import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import CustomGridToolbar from '../../components/datagrid/GridToolBar';
import { DataGrid } from '@mui/x-data-grid';
import { academicCalendarTableHeader } from '../../components/datagrid/academicCalendar/academicCalendarTableHeader';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useLocation } from 'react-router-dom';
import LeadDetails from './LeadDetails';
import MarkAttendanceModalContent from './MarkAttendanceModalContent';
import { getSessionPreferencesSync } from '../../redux/features/croSlice';
import useHandleApiResponse from '../../Hooks/useHandleApiResponse';
import CircularProgress from '@mui/material/CircularProgress';
import WarningPopup from '../cro/myworkspace/CROleads/components/WarningPopup';
import { isSameUserType } from '../../utils/helperFunction';
import { smeAndSmoTypes } from '../../utils/employeeTypes';
import { useReactToPrint } from 'react-to-print';

const AcademicCalendarView = ({ publicRoute }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const academicCalendar = useSelector((state) => state.crd.academicCalendar);
  const loading = useSelector((state) => state.crd.loading);
  const rescheduledCalendars = useSelector((state) => state.crd.rescheduledCalendars);
  const subjectEnrollmentIds = useSelector((state) => state.crd.subjectEnrollmentIds);
  const leadDetails = useSelector((state) => state.crd.leadDetails);

  const syncLoading = useSelector((state) => state.cro.syncLoading);

  const [open, setOpen] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false); //SETTING STATE TO AVOID UNWANTED ANIMATION WHILE RENDERING
  const [gridSize, setGridSize] = useState(10);
  const pathParts = location.pathname.split('/');
  const subjectEnrollmentID = pathParts[4] || null;
  const tutorID = pathParts[5] || null;
  const [refresh, setRefresh] = useState(false);
  const [warning, setWarning] = useState(false);

  const isSmeOrSmo = useMemo(() => {
    const role = JSON.parse(localStorage.getItem('cred'))?.profile?.role;
    return isSameUserType({ roleArray: smeAndSmoTypes, role });
  }, []);

  const apiHelperHandle = useHandleApiResponse();
  const queryParams = new URLSearchParams(location.search);
  const leadIdFromQuery = queryParams.get('leadId');
  const [seletctedSubEnrollmentId, setseletctedSubEnrollmentId] = useState();
  const [globalModalOpen, setGlobalModalOpen] = useState(false);
  const [selectedModalContent, setSelectedModalContent] = useState();
  const [selectedItem, setSelectedItem] = useState();

  // Printing setup: compact table view for download
  const printRef = useRef();
  const reactToPrintFn = useReactToPrint({
    content: () => printRef.current,
  });

  const PrintableCalendar = React.memo(({ data }) => (
    <Box ref={printRef} sx={{ p: 2 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Session</th>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Date</th>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Time</th>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Topic</th>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {Array.isArray(data) &&
            data.map((item) => {
              const status = !item.trainerAttendanceID && item.rescheduled_id
                ? 'RESCHEDULED'
                : item.trainerAttendanceID
                  ? 'COMPLETED'
                  : item.reschedule_requested_by == 1 && !item.rescheduled_at
                    ? 'RESCHEDULE REQUESTED'
                    : 'PENDING';
              const topicData = item.academic_calendar_data
                ? `Chapter: ${item.academic_calendar_data.chapter || 'N/A'}\nTopic: ${item.academic_calendar_data.topic || 'N/A'}\nObjectives: ${item.academic_calendar_data.objectives || 'N/A'}`
                : 'N/A';
              return (
                <tr key={item.id}>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.session_no}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{dayjs(item.session_date).format('YYYY ddd, MMM D')}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{dayjs(item.from_time, 'HH:mm:ss').format('hh:mm A')} - {dayjs(item.to_time, 'HH:mm:ss').format('hh:mm A')}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{topicData}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{status}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </Box>
  ));
  useEffect(() => {
    if (!publicRoute) {
      // CALL FOR SUBJECT ENROLMENT IDS AGAINST LEAD ID
      dispatch(getSubjectEnrollmentIds(leadIdFromQuery));
    } else if (publicRoute) {
      //IF PUBLIC ROUTE IT WILL CALL THE CALENDAR WRT TO THE PARAMS GETTING FROM THE OLD PORTAL
      dispatch(
        getAcademicCalendar({
          subject_enrollment_id: subjectEnrollmentID,
          tutorID: tutorID,
        }),
      );
    }
  }, [leadIdFromQuery, publicRoute]);

  useEffect(() => {
    if (subjectEnrollmentIds.length && !seletctedSubEnrollmentId) {
      setseletctedSubEnrollmentId(subjectEnrollmentIds[0].subject_enrollment_id);
    }
    if (seletctedSubEnrollmentId) {
      dispatch(
        getAcademicCalendar({
          subject_enrollment_id: seletctedSubEnrollmentId,
          tutorID: null,
        }),
      );
    }
  }, [seletctedSubEnrollmentId, subjectEnrollmentIds, refresh]);

  useEffect(() => {
    if (publicRoute) {
      dispatch(getLeadMinDetails(subjectEnrollmentID));
    } else if (!publicRoute && seletctedSubEnrollmentId) {
      //api call only after the subjectenrollmentId is set
      dispatch(getLeadMinDetails(seletctedSubEnrollmentId));
    }

    //CALL FOR LEAD NAME AND GET DETAILS
  }, [subjectEnrollmentID, seletctedSubEnrollmentId]);

  const boxClickFunction = useCallback((item) => {
    //CALLBACK FUNCTION TO OPEN DIALOG BOX IT ONLY OPEN THE DIALOG BOX IF THE SESSION IS RESCHEDULED AND IT WILL CALL API FOR TO LOG RESCHEDULED SESSIONS DETAILAS
    if (item.rescheduled_id) {
      setOpen(true);
      dispatch(getRescheduledCalendars(item.session_schedule_id));
    }
  }, []);

  const boxCloseFuntion = () => {
    setOpen(false);
  };
  const handleEnrolmentIdChange = (event, option) => {
    setseletctedSubEnrollmentId(option.subject_enrollment_id);
  };

  const attendanceMarkModalFunction = (item) => {
    setSelectedModalContent('Mark Attendance');
    setSelectedItem(item);
    setGlobalModalOpen(true);
  };

  const handleGlobalDialogClose = () => {
    setSelectedModalContent(null);
    setSelectedItem(null);
    setGlobalModalOpen(false);
  };
  const BoxDesign = React.memo(({ item }) => {
    const backgroundColor =
      !item.trainerAttendanceID && item.rescheduled_id //NO TRAINER ID AND PRESENCE OF RESCHEDULED ID MEANS THE OBJECT IS RESCHEDULED SO MAKE THE COLOR TO YELLOW
        ? '#fce9034a'
        : item.trainerAttendanceID // IF TRAINER ID IS PRESENT THEN MAKE THE COLOR TO GREEN ELSE BLUE
          ? '#41fc032e'
          : item.reschedule_requested_by == 1 && !item.rescheduled_at
            ? '#ff00004d'
            : '#03a9fc2e';
    return (
      <motion.div
        key={item.id} // Ensures animation runs only once per unique item
        initial={!hasAnimated ? { rotateY: 180 } : false} // Only animate initially
        animate={!hasAnimated ? { rotateY: 0 } : false} // Stop animation after first render
        transition={{ duration: 0.5 }}
        style={{ perspective: '1000px' }}
        onAnimationComplete={() => setHasAnimated(true)} // Set flag after first animation
      >
        <Paper
          onClick={() => boxClickFunction(item)}
          elevation={3}
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: backgroundColor,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            minHeight: '130px',
            transition: 'transform 0.3s ease, border 0.3s ease',
            '&:hover': {
              border: item.rescheduled_id ? '0.5px solid black' : '',
              transform: item.rescheduled_id ? 'scale(1.05)' : '',
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100px',
              height: '30px',
              bgcolor:
                !item.trainerAttendanceID && item.rescheduled_id //SIDE BANNER COLOR MAKING TO DARK TO SHOW COMPLETED, PENDING, RESCHEDULED
                  ? '#fce903' //YELLOW RESCHEDULED
                  : item.trainerAttendanceID
                    ? '#41fc0378' //GREEN DONE
                    : item.reschedule_requested_by == 1 && !item.rescheduled_at
                      ? 'RED'
                      : '#03a9fc75', //PENDING BLUE
              color: 'white',
              fontSize: '8px',
              fontWeight: 'bold',
              textAlign: 'center',
              transform: 'rotate(-45deg) translate(-20px, -10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingRight: '7px',
            }}
          >
            {!item.trainerAttendanceID && item.rescheduled_id
              ? 'RESCHEDULED'
              : item.trainerAttendanceID
                ? 'COMPLETED'
                : item.reschedule_requested_by == 1 && !item.rescheduled_at
                  ? 'RESCHEDULE REQUESTED'
                  : 'PENDING'}
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              cursor: 'pointer',
            }}
          >
            {/* MARK ATTENDANCE BUTTON SECTION */}
            {!item.trainerAttendanceID && !publicRoute && !isSmeOrSmo && (
              <Box
                sx={{
                  borderRadius: '50%',
                  px: 0.5,
                  pt: 0.5,
                  border: '1px solid transparent',
                  '&:hover': {
                    border: '1px solid black',
                  },
                }}
                onClick={() => attendanceMarkModalFunction(item)}
              >
                <Tooltip title="Mark Attendance">
                  <Icon icon="mdi:checkbox-marked-circle-plus-outline" width="20" height="20" />
                </Tooltip>
              </Box>
            )}

            {item.academic_calendar_data?.session_no && ( //TOOLTIP TO SHOW THE SESSION DETAILS ON HOVER,DATA IS AVAILBALE ONLY FOR COMPLETED SESSIONS
              <Tooltip
                title={
                  <Box
                    sx={{
                      bgcolor: 'white',
                      color: 'black',
                      p: 1,
                      borderRadius: '4px',
                      boxShadow: 3,
                      minWidth: '120px',
                    }}
                  >
                    <Typography variant="body2">
                      <strong>Chapter : </strong>
                      {item.academic_calendar_data?.chapter}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Topic : </strong>
                      {item.academic_calendar_data?.topic}
                    </Typography>
                    <Typography variant="body2">
                      <strong> Objectives : </strong>
                      {item.academic_calendar_data?.objectives}
                    </Typography>
                  </Box>
                }
                arrow
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <Icon icon="mdi:eye" width="20" height="20" />
                </Box>
              </Tooltip>
            )}
          </Box>

          <Typography mt={1} fontWeight="bold" color="primary">
            {dayjs(item.session_date).format('YYYY ddd, MMM D')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {dayjs(item.from_time, 'HH:mm:ss').format('hh:mm A')} - {dayjs(item.to_time, 'HH:mm:ss').format('hh:mm A')}
          </Typography>

          <Typography variant="button" color="textSecondary">
            Session {item.session_no}
          </Typography>
          {item?.type && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography
                variant="caption"
                color={item?.type?.toLowerCase().includes('exam') ? '#df4d51' : '#63738c'}
                sx={{
                  fontStyle: 'italic',
                  fontWeight: 'bold',
                  // textShadow: '0.5px 0.5px 0 black'
                }}
              >
                {item?.type || 'Class'}
              </Typography>
            </Box>
          )}
        </Paper>
      </motion.div>
    );
  });

  return (
    <GlobalWrapper
      title="Academic Calendar"
      actions={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={() => {
              if (!academicCalendar?.length) return;
              setWarning(true);
            }}
            color="info"
            startIcon={syncLoading ? <CircularProgress color="primary" size={'20px'} /> : <SyncIcon />}
            variant="contained"
            disabled={syncLoading || isSmeOrSmo}
          >
            {syncLoading ? 'Syncing...' : 'Sync'}
          </Button>

          <Button
            onClick={() => {
              if (!academicCalendar?.length) return;
              reactToPrintFn();
            }}
            color="primary"
            startIcon={<Icon icon="mdi:printer" width="20" height="20" />}
            variant="outlined"
            disabled={!academicCalendar?.length}
          >
            Download
          </Button>
        </Box>
      }
    >
      <div style={{ position: 'fixed', left: '-10000px', top: 0 }}>
        <PrintableCalendar data={academicCalendar} />
      </div>

      <GlobalDialog
        content={
          <WarningPopup
            handleSubmit={() => {
              dispatch(
                getSessionPreferencesSync({ subject_enrollment_id: academicCalendar?.[0].subject_enrollment_id }),
              ).then((res) =>
                apiHelperHandle(res, {
                  onSuccess: () => {
                    setWarning(false);
                    setWarning(false);
                  },
                }),
              );
            }}
            values={warning}
            loading={syncLoading}
          />
        }
        setOpen={setWarning}
        open={Boolean(warning)}
        closeButton
      />
      <Box>
        {loading ? (
          <Box>
            <Loader3D />
          </Box>
        ) : (
          <Box>
            {!publicRoute && (
              <Box display="flex" justifyContent={'space-between'} gap={2} alignItems={'center'}>
                <Box mb={1} width="100%">
                  <Autocomplete
                    fullWidth
                    disabled={loading}
                    name="subject"
                    id="subject"
                    options={Array.isArray(subjectEnrollmentIds) ? subjectEnrollmentIds : []}
                    getOptionLabel={(sub) => sub?.subject_name?.concat(' (', sub.course_name, ')') || ''}
                    value={
                      (subjectEnrollmentIds.length &&
                        subjectEnrollmentIds?.find((sub) => sub?.subject_enrollment_id == seletctedSubEnrollmentId)) ||
                      null
                    }
                    onChange={handleEnrolmentIdChange}
                    renderInput={(params) => (
                      <TextField disabled fullWidth autoComplete="off" {...params} label="Subjects" />
                    )}
                  />
                </Box>
              </Box>
            )}
            <Box mb={1}>
              <LeadDetails leadDetails={leadDetails} seletctedSubEnrollmentId={seletctedSubEnrollmentId} />
            </Box>
            {academicCalendar.length == 0 ? (
              <Box mt={10}>
                <NoDataFound />
              </Box>
            ) : (
              <Paper elevation={6} sx={{ p: 2, borderRadius: 2 }}>
                <Grid container spacing={2}>
                  {academicCalendar.length &&
                    academicCalendar.map((item) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                        <BoxDesign item={item} />
                      </Grid>
                    ))}
                </Grid>
              </Paper>
            )}
          </Box>
        )}
      </Box>
      <GlobalDialog
        open={open}
        setOpen={setOpen}
        maxWidth={'lg'}
        fullWidth
        title={'Rescheduled Details'}
        closeButton={true}
        closeCondition={boxCloseFuntion}
        content={
          <>
            <Box>
              <DataGrid
                components={{ Toolbar: CustomGridToolbar }}
                rows={rescheduledCalendars}
                columns={academicCalendarTableHeader}
                loading={loading}
                getRowId={(row) => row.session_schedule_id}
                pageSize={gridSize}
                autoHeight
                rowsPerPageOptions={[5, 10, 25, 50]}
                onPageSizeChange={(newGridSize) => setGridSize(newGridSize)}
              />
            </Box>
          </>
        }
      />

      <GlobalDialog
        open={globalModalOpen}
        setOpen={setGlobalModalOpen}
        maxWidth={'sm'}
        fullWidth
        title={selectedModalContent ? selectedModalContent : ''}
        closeButton={true}
        closeCondition={handleGlobalDialogClose}
        content={
          <>
            <Box>
              {selectedModalContent === 'Mark Attendance' && (
                <MarkAttendanceModalContent
                  open={globalModalOpen}
                  setOpen={setGlobalModalOpen}
                  row={selectedItem}
                  apiCall={getAcademicCalendar}
                  dispatchParams={
                    publicRoute
                      ? {
                          subject_enrollment_id: subjectEnrollmentID,
                          tutorID: tutorID,
                        }
                      : {
                          subject_enrollment_id: seletctedSubEnrollmentId,
                          tutorID: null,
                        }
                  }
                />
              )}
            </Box>
          </>
        }
      />
    </GlobalWrapper>
  );
};

export default AcademicCalendarView;
